import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import { useAuth } from '../contexts/AuthContext';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-rowing-gray-50">
      <Navigation isAuthenticated={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-display font-bold text-rowing-navy mb-2">Welcome back!</h1>
          {user && <p className="text-rowing-gray-600 mb-8">Logged in as: {user.email}</p>}

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-xl shadow-sm border border-rowing-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rowing-gray-600 text-sm">Saved Schools</p>
                  <p className="text-3xl font-bold text-rowing-navy mt-1">0</p>
                </div>
                <div className="w-12 h-12 bg-rowing-blue bg-opacity-10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-rowing-blue"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-xl shadow-sm border border-rowing-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rowing-gray-600 text-sm">Profile Views</p>
                  <p className="text-3xl font-bold text-rowing-navy mt-1">0</p>
                </div>
                <div className="w-12 h-12 bg-rowing-red bg-opacity-10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-rowing-red"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-6 rounded-xl shadow-sm border border-rowing-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rowing-gray-600 text-sm">Messages</p>
                  <p className="text-3xl font-bold text-rowing-navy mt-1">0</p>
                </div>
                <div className="w-12 h-12 bg-rowing-sky bg-opacity-10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-rowing-sky"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white rounded-xl shadow-sm border border-rowing-gray-100 p-8"
          >
            <h2 className="text-xl font-semibold text-rowing-navy mb-6">Get Started</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/profile"
                className="text-left p-4 rounded-lg border-2 border-rowing-gray-200 hover:border-rowing-blue transition-colors block"
              >
                <div className="flex items-center mb-2">
                  <div className="w-10 h-10 bg-rowing-blue bg-opacity-10 rounded-full flex items-center justify-center mr-3">
                    <svg
                      className="w-5 h-5 text-rowing-blue"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-medium text-rowing-gray-900">Complete Profile</h3>
                </div>
                <p className="text-sm text-rowing-gray-600">
                  Add your rowing experience and academic info
                </p>
              </Link>

              <Link
                to="/schools"
                className="text-left p-4 rounded-lg border-2 border-rowing-gray-200 hover:border-rowing-blue transition-colors block"
              >
                <div className="flex items-center mb-2">
                  <div className="w-10 h-10 bg-rowing-red bg-opacity-10 rounded-full flex items-center justify-center mr-3">
                    <svg
                      className="w-5 h-5 text-rowing-red"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <h3 className="font-medium text-rowing-gray-900">Browse Schools</h3>
                </div>
                <p className="text-sm text-rowing-gray-600">Explore 200+ college rowing programs</p>
              </Link>

              <button className="text-left p-4 rounded-lg border-2 border-rowing-gray-200 hover:border-rowing-blue transition-colors">
                <div className="flex items-center mb-2">
                  <div className="w-10 h-10 bg-rowing-sky bg-opacity-10 rounded-full flex items-center justify-center mr-3">
                    <svg
                      className="w-5 h-5 text-rowing-sky"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-medium text-rowing-gray-900">Track Performance</h3>
                </div>
                <p className="text-sm text-rowing-gray-600">Log your erg scores and race results</p>
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
